import React, { useState } from "react";
import { MdAnalytics, MdInventory, MdPayments } from "react-icons/md";
import {
  FaPlus,
  FaArrowRight,
  FaStore,
  FaShoppingCart,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCheckCircle,
  FaRocket,
  FaBolt,
  FaUsers,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import AddBusinessModal from "../../components/modals/AddBusinessDrawer";

const DashboardMockup = () => (
  <div className="relative w-full max-w-md mx-auto lg:ml-auto">
    <div className="absolute inset-0 bg-gradient-to-tr from-primary/25 to-secondary/35 rounded-3xl blur-3xl scale-110 pointer-events-none"></div>
    <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200/80">
      <div className="bg-gray-100 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
          <div className="w-3 h-3 rounded-full bg-green-400"></div>
        </div>
        <div className="flex-1 bg-white rounded px-3 py-1 text-[11px] text-gray-400 border border-gray-200 ml-2 truncate">
          merchant.zapp.com/dashboard
        </div>
      </div>
      <div className="flex">
        <div className="w-12 bg-primary flex flex-col items-center py-4 gap-3 flex-shrink-0">
          <div className="w-6 h-6 bg-white/20 rounded-lg"></div>
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`w-6 h-1.5 rounded-full ${i === 1 ? "bg-secondary" : "bg-white/25"}`}
            ></div>
          ))}
        </div>
        <div className="flex-1 bg-gray-50 p-3 min-w-0">
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              { label: "Revenue", value: "D12.4K", delta: "+18%", deltaColor: "text-green-600" },
              { label: "Orders", value: "284", delta: "+12%", deltaColor: "text-blue-600" },
              { label: "Customers", value: "1.2K", delta: "+9%", deltaColor: "text-purple-600" },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
                <p className="text-[9px] text-gray-500">{stat.label}</p>
                <p className="text-sm font-bold text-gray-900 leading-snug">{stat.value}</p>
                <p className={`text-[9px] font-semibold ${stat.deltaColor}`}>{stat.delta}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100 mb-2">
            <p className="text-[9px] font-semibold text-gray-700 mb-2">Sales Overview</p>
            <div className="flex items-end gap-0.5 h-14">
              {[35, 55, 40, 70, 50, 80, 60, 90, 65, 75, 55, 95].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t"
                  style={{
                    height: `${h}%`,
                    backgroundColor:
                      i === 11 ? "#5e033e" : i % 3 === 0 ? "#FFD43A99" : "#5e033e33",
                  }}
                ></div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
            <p className="text-[9px] font-semibold text-gray-700 mb-1.5">Recent Orders</p>
            {[
              { id: "#1042", amount: "D54.00", status: "Delivered", statusClass: "bg-green-50 text-green-700" },
              { id: "#1041", amount: "D120.00", status: "Processing", statusClass: "bg-yellow-50 text-yellow-700" },
              { id: "#1040", amount: "D36.50", status: "Shipped", statusClass: "bg-blue-50 text-blue-700" },
            ].map((order, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0 gap-1"
              >
                <span className="text-[9px] text-gray-600 font-medium">{order.id}</span>
                <span className="text-[9px] font-bold text-gray-900">{order.amount}</span>
                <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${order.statusClass}`}>
                  {order.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="absolute -top-3 -right-3 bg-green-500 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-xl flex items-center gap-1.5 animate-bounce">
      <FaCheckCircle className="text-[9px]" />
      New Order!
    </div>
    <div className="absolute -bottom-3 -left-3 bg-primary text-secondary text-[11px] font-bold px-3 py-1.5 rounded-full shadow-xl flex items-center gap-1.5">
      💰 +D240 Today
    </div>
  </div>
);

const OnboardingScreen = () => {
  const [isCreateBusinessOpen, setIsCreateBusinessOpen] = useState(false);

  const features = [
    {
      icon: <MdAnalytics />,
      title: "Real-time Analytics",
      description:
        "Live dashboards with sales trends, revenue tracking, and customer insights across all your businesses.",
      iconBg: "bg-violet-50",
      iconColor: "text-violet-600",
    },
    {
      icon: <FaShoppingCart />,
      title: "Order Management",
      description:
        "Process, track, and fulfill orders seamlessly. Automated notifications keep your customers in the loop.",
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      icon: <MdInventory />,
      title: "Inventory Control",
      description:
        "Track stock levels in real-time, set reorder alerts, and manage multiple product variants with ease.",
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      icon: <FaUsers />,
      title: "Customer Management",
      description:
        "Build lasting relationships. View purchase history, manage contacts, and understand your audience.",
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      icon: <MdPayments />,
      title: "Secure Payments",
      description:
        "Accept payments from multiple channels. Every transaction is encrypted and fully protected.",
      iconBg: "bg-teal-50",
      iconColor: "text-teal-600",
    },
    {
      icon: <FaCalendarAlt />,
      title: "Event Management",
      description:
        "Create events, sell tickets, manage attendees, and track revenue — all in one place.",
      iconBg: "bg-pink-50",
      iconColor: "text-pink-600",
    },
  ];

  const businessTypes = [
    {
      icon: <FaShoppingCart />,
      title: "Shopping",
      subtitle: "E-commerce & Retail",
      description:
        "Launch your online store, manage products, process orders, and track inventory in real-time.",
      gradient: "from-blue-500 to-indigo-600",
      features: [
        "Product catalog management",
        "Order tracking & fulfillment",
        "Customer reviews & ratings",
        "Discount & promo codes",
      ],
    },
    {
      icon: <FaCalendarAlt />,
      title: "Events",
      subtitle: "Ticketing & Management",
      description:
        "Host events of any size. Sell tickets, manage attendees, and create unforgettable experiences.",
      gradient: "from-[#5e033e] to-pink-700",
      features: [
        "Event creation & listing",
        "Ticket sales & QR codes",
        "Attendee management",
        "Revenue analytics",
      ],
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Pay Bills",
      subtitle: "Payments & Services",
      description:
        "Offer bill payment services to customers. Manage subscriptions and recurring payments with ease.",
      gradient: "from-green-500 to-teal-600",
      features: [
        "Bill payment processing",
        "Subscription management",
        "Payment history tracking",
        "Automated receipts",
      ],
    },
  ];

  const steps = [
    {
      number: "✓",
      title: "Create Your Account",
      description: "Account setup complete. You're already in!",
      state: "done",
    },
    {
      number: "2",
      title: "Set Up Your Business",
      description: "Add your business name, category, location, and brand assets.",
      state: "active",
    },
    {
      number: "3",
      title: "Add Products or Services",
      description: "List your products, set pricing, and upload images.",
      state: "pending",
    },
    {
      number: "4",
      title: "Go Live & Start Selling",
      description: "Publish your business and start accepting orders.",
      state: "pending",
    },
  ];

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-white to-secondary/10 min-h-[calc(100vh-64px)] flex items-center">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-secondary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center w-full">
          <div>
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <HiSparkles />
              Welcome to ZApp Merchant Portal
            </div>
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.1] mb-6">
              The Smarter Way to{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-600">
                Run Your Business
              </span>
            </h1>
            <p className="text-xl text-gray-500 leading-relaxed mb-8 max-w-lg">
              Launch, manage, and grow your business with ZApp's all-in-one merchant platform.
              Analytics, orders, payments — everything under one roof.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={() => setIsCreateBusinessOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-bold text-base rounded-xl hover:bg-primary/90 transition-all transform hover:scale-105 shadow-lg shadow-primary/25 w-full sm:w-auto"
              >
                <FaPlus />
                Create Your First Business
                <FaArrowRight />
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-400">
              {["Free to get started", "No hidden fees", "5-minute setup"].map((text, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-green-500 flex-shrink-0" />
                  {text}
                </span>
              ))}
            </div>
          </div>
          <div className="hidden lg:block">
            <DashboardMockup />
          </div>
        </div>
      </section>

      <section className="bg-primary py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            { value: "500+", label: "Active Merchants" },
            { value: "3", label: "Business Models" },
            { value: "Real-time", label: "Data & Analytics" },
            { value: "24/7", label: "Platform Uptime" },
          ].map((stat, i) => (
            <div key={i}>
              <p className="text-3xl font-extrabold text-secondary mb-1">{stat.value}</p>
              <p className="text-sm text-white/60 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <FaBolt /> Platform Features
            </div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              Everything you need, nothing you don't
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              A powerful, focused toolkit built for merchants who want to spend less time managing
              and more time growing.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-lg hover:border-primary/20 transition-all duration-300 group"
              >
                <div
                  className={`inline-flex p-3 rounded-xl text-2xl ${feature.iconBg} ${feature.iconColor} mb-5 group-hover:scale-110 transition-transform duration-200`}
                >
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-secondary/20 text-yellow-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <FaRocket /> Getting Started
            </div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              Up and running in minutes
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Four simple steps from account creation to your first sale.
            </p>
          </div>
          <div className="relative grid md:grid-cols-4 gap-8">
            <div className="hidden md:block absolute top-8 left-[12%] right-[12%] h-px bg-gradient-to-r from-green-300 via-primary/30 to-gray-200"></div>
            {steps.map((step, i) => (
              <div key={i} className="flex flex-col items-center text-center relative">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-extrabold mb-5 z-10 border-2 shadow-md transition-all ${
                    step.state === "done"
                      ? "bg-green-500 border-green-500 text-white shadow-green-100"
                      : step.state === "active"
                      ? "bg-primary border-primary text-white shadow-primary/20 ring-4 ring-primary/15"
                      : "bg-white border-gray-200 text-gray-400"
                  }`}
                >
                  {step.number}
                </div>
                <h3
                  className={`text-base font-bold mb-2 ${
                    step.state === "done"
                      ? "text-green-700"
                      : step.state === "active"
                      ? "text-primary"
                      : "text-gray-400"
                  }`}
                >
                  {step.title}
                </h3>
                <p className="text-sm text-gray-400">{step.description}</p>
                {step.state === "active" && (
                  <button
                    onClick={() => setIsCreateBusinessOpen(true)}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
                  >
                    Start here <FaArrowRight className="text-xs" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="business-types" className="py-24 bg-gradient-to-br from-primary/5 to-secondary/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4 shadow-sm border border-primary/20">
              <FaStore /> Business Types
            </div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              One platform, three powerful models
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Whether you sell products, host events, or offer payment services — ZApp is built
              for you.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {businessTypes.map((type, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 h-full flex flex-col"
              >
                <div className={`bg-gradient-to-br ${type.gradient} p-8 text-white`}>
                  <div className="text-4xl mb-4 opacity-90">{type.icon}</div>
                  <h3 className="text-2xl font-extrabold mb-1">{type.title}</h3>
                  <p className="text-white/70 text-sm font-medium">{type.subtitle}</p>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{type.description}</p>
                  <ul className="space-y-3">
                    {type.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-gray-700">
                        <FaCheckCircle className="text-green-500 flex-shrink-0 text-base" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setIsCreateBusinessOpen(true)}
                    className="mt-6  w-full py-3 rounded-xl border-2 border-gray-100 text-gray-700 text-sm font-bold hover:border-primary hover:text-primary transition-colors"
                  >
                    Get Started →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 bg-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <HiSparkles className="text-secondary text-5xl mx-auto mb-6" />
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
            Ready to launch your business?
          </h2>
          <p className="text-white/65 text-xl mb-10 max-w-xl mx-auto leading-relaxed">
            Join hundreds of merchants already growing with ZApp. Get started in minutes — no
            technical knowledge needed.
          </p>
          <button
            onClick={() => setIsCreateBusinessOpen(true)}
            className="inline-flex items-center gap-3 px-10 py-5 bg-secondary text-gray-900 font-extrabold text-lg rounded-2xl hover:bg-secondary/90 transform hover:scale-105 transition-all shadow-2xl shadow-black/20"
          >
            <FaPlus />
            Create Your First Business
            <FaArrowRight />
          </button>
          <p className="text-white/35 text-sm mt-6">Free to start · No credit card required</p>
        </div>
      </section>

      <footer className="bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            © 2026 ZApp by Zahra Group Holding. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>Secure & Reliable</span>
            <span className="text-gray-700">·</span>
            <span>24/7 Support</span>
            <span className="text-gray-700">·</span>
            <span>Multi-business Management</span>
          </div>
        </div>
      </footer>

      <AddBusinessModal
        isOpen={isCreateBusinessOpen}
        onClose={() => setIsCreateBusinessOpen(false)}
      />
    </div>
  );
};

export default OnboardingScreen;
